import Route from '@core/components/Route';
import { RoutePath } from '@core/types/route';
import { H2, H3 } from '@shared/components/Heading';
import List from '@shared/components/List';
import ListItem from '@shared/components/ListItem';
import Paragraph from '@shared/components/Paragraph';
import Section from '@shared/components/Section';
import { Spacing } from '@shared/theme/type';
import { FC, memo } from 'react';

import { SectionDark1, SectionGold } from '../../components/ColoredSection';
import GenericLayout from '../../components/GenericLayout';
import PageImage from '../../components/PageImage';
import RouteButton from '../../components/RouteButton';
import { ListItemHeader } from '../../styled/instructor-program';
import { PageImageId } from '../../types/page-image';
import { COLOR } from '../../types/theme';

const MARGIN_H3: Spacing = { bottom: '1rem', top: '2.25rem' };

const InstructorProgram: FC = () => (
  <GenericLayout title='Zertifizierung'>
    <PageImage id={PageImageId.InstructorProgram} />

    <SectionDark1>
      <H2>Instructor-Lizenzen im Systema – Dein Weg zur Lehrkarriere</H2>
      <Paragraph>
        Möchtest Du Deine Leidenschaft für die altrussische Kampfkunst Systema in eine Karriere
        verwandeln? Unser exklusives Instructor-Lizenzierungsprogramm bietet Dir die Möglichkeit,
        Deine Fähigkeiten zu perfektionieren und andere zu inspirieren. Mit einem klar
        strukturierten Pfad und vier Stufen von Lizenzen begleiten wir Dich von den ersten Schritten
        bis zur Führung einer ganzen Region.
      </Paragraph>

      <H3>Die vier Stufen des Instructor-Programms:</H3>
      <List numbers>
        <ListItem>
          <ListItemHeader>Associate Instructor</ListItemHeader> Diese Einstiegsstufe richtet sich an
          angehende Trainer, die ihre Fähigkeiten im Unterrichten weiterentwickeln möchten. Hier
          arbeitest Du noch unter Anleitung eines erfahreneren Ausbilders, während Du Deine eigenen
          Fähigkeiten vertiefst und lernst, effektiv zu unterrichten.
        </ListItem>
        <ListItem>
          <ListItemHeader>Instructor</ListItemHeader> Als Instructor bist Du voll qualifiziert,
          selbstständig Systema-Klassen zu leiten. Du hast alle notwendigen Fähigkeiten, um Schüler
          zu trainieren und ihnen die Philosophie und Techniken von Systema nahezubringen. Der
          perfekte Schritt für alle, die ihre eigene Gruppe führen wollen.
        </ListItem>
        <ListItem>
          <ListItemHeader>Senior Instructor</ListItemHeader> Diese Stufe ist für erfahrene
          Instructoren, die bereit sind, mehr Verantwortung zu übernehmen. Sie coachen nicht nur
          Schüler, sondern auch andere Instructoren, und tragen so zur Weiterentwicklung der
          gesamten Community bei.
        </ListItem>
        <ListItem>
          <ListItemHeader>Director</ListItemHeader> Auf höchster Ebene leitst Du als Director nicht
          nur Klassen, sondern managst ganze Trainingszentren oder Regionen. Du übernimmst eine
          Führungsrolle und gestaltest die Zukunft des Systema in Deiner Region aktiv mit.
        </ListItem>
      </List>
    </SectionDark1>

    <Section>
      <H2>Warum eine Systema-Instructor-Lizenz?</H2>
      <Paragraph>
        Systema ist mehr als nur eine <Route href={RoutePath.Kampfkunst}>Kampfkunst</Route> – es ist
        eine Philosophie, die Körper und Geist vereint. Als zertifizierter Instructor hast Du die
        Möglichkeit, dieses Wissen weiterzugeben und Menschen dabei zu helfen, ihre körperliche und
        mentale Stärke zu entfalten. Du erhältst Zugang zu exklusiven Weiterbildungen, einem
        globalen Netzwerk und kontinuierlicher Unterstützung, um Deine Karriere erfolgreich
        aufzubauen.
      </Paragraph>
    </Section>

    <SectionDark1>
      <H2>Dein Weg zur Associate Instructor Lizenz</H2>

      <Paragraph>
        Unser intensiver zweijähriger Ausbilderkurs ist der perfekte Einstieg für alle, die Systema
        auf professionellem Niveau unterrichten wollen. Der Kurs wird von Meister Falko Zander
        persönlich geleitet und verspricht zwei klare Ergebnisse:
      </Paragraph>

      <List numbers>
        <ListItem>
          <ListItemHeader>
            Den <Route href={RoutePath.KampfkunstRanking}>3. Schülergrad</Route> in Solovev Defense:
          </ListItemHeader>
          Erwirb ein tiefgehendes Verständnis für die körperlichen und mentalen Prinzipien von
          Systema, einschließlich der wichtigsten Grundlagen der Körperstruktur.
        </ListItem>
        <ListItem>
          <ListItemHeader>Die Associate Instructor Lizenz:</ListItemHeader> Lege den ersten
          Grundstein, um Schülern Systema näherzubringen und sie auf ihrem eigenen Weg zu begleiten.
        </ListItem>
      </List>

      <Paragraph>
        Der Kurs ist so konzipiert, dass er sich flexibel in Deinen Alltag integrieren lässt. Die
        Ausbildung findet grundsätzlich an Wochenenden in Berlin statt, sodass Du Dich auch als
        Berufstätiger oder Student weiterbilden kannst.
      </Paragraph>

      <H3 margin={MARGIN_H3}>Vorteile des Kurses:</H3>
      <List bullets>
        <ListItem>
          Erfahrene Leitung: Lerne von Meister Falko Zander, dem Gründer von "Solovev Defense".
        </ListItem>
        <ListItem>
          Strukturierte Ausbildung: Profitiere von einem klaren Lehrplan, der Dich in allen
          Bereichen der Kampfkunst und des Unterrichtens weiterbringt.
        </ListItem>{' '}
        <ListItem>
          Exklusives Netzwerk: Werde Teil eines internationalen Netzwerks aus Ausbildern und
          Schülern.
        </ListItem>
        <ListItem>
          Berufliche Perspektiven: Mit der Associate Instructor Lizenz legst Du die Basis für eine
          Karriere im Kampfsport – ob als Trainer in einem Studio oder als Gründer einer eigenen
          Schule.
        </ListItem>
      </List>

      <H3 margin={MARGIN_H3}>Kursdetails:</H3>
      <List bullets>
        <ListItem>Dauer: 2 Jahre</ListItem>
        <ListItem>Veranstaltungsort: Berlin</ListItem>
        <ListItem>Zeit: 8 Wochenenden / Jahr</ListItem>
        <ListItem>Kosten: 3.192 € (inklusive Prüfungsgebühren, T-Shirts und Lehrvideos)</ListItem>
      </List>

      <H3 margin={MARGIN_H3}>Teilnahmebedingungen:</H3>
      <List>
        <ListItem>Bezahlung: Quartalsweise im Voraus 399 €.</ListItem>
        <ListItem>
          Verlängerung: Bei Bedarf kann die Ausbildung um maximal sechs Monate verlängert werden.
          Die Verlängerung ist kostenpflichtig.
        </ListItem>
        <ListItem>
          Eigenverantwortliches Üben: Der Kurs setzt voraus, dass der Teilnehmer die Inhalte
          eigenständig übt und perfektioniert.
        </ListItem>
      </List>
    </SectionDark1>

    <SectionGold>
      <H2>Starte Deinen Weg zur Lehrkarriere</H2>
      <Paragraph>
        Nutze diese einmalige Gelegenheit, Systema auf einem höheren Niveau zu erlernen und
        weiterzugeben. Jetzt anmelden und den ersten Schritt zu einer erfüllenden Trainerkarriere
        machen!
      </Paragraph>
      <Paragraph>Sichere Dir Deinen Platz und starte Deine Reise als Systema-Instructor!</Paragraph>
      <RouteButton background={COLOR.White} bold color={COLOR.Gold2} href={RoutePath.Kontakt}>
        Bewirb Dich jetzt!
      </RouteButton>
    </SectionGold>
  </GenericLayout>
);

export default memo(InstructorProgram);
